import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  CheckCircle,
  Calendar,
  MapPin,
  User,
  Mail,
  Phone,
  FileText,
  Loader2,
  AlertCircle,
  Home,
  Printer,
  Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NotaryConfirmation = () => {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bookingId = searchParams.get('booking_id');
  const sessionId = searchParams.get('session_id');
  const paymentIntentId = searchParams.get('payment_intent');

  useEffect(() => {
    const confirmBooking = async () => {
      if (!bookingId) {
        setError('No booking ID provided');
        setLoading(false);
        return;
      }

      try {
        // PaymentIntent flow (V2 dark Payment Element) — uses confirm-intent
        // Checkout Session flow (legacy) — uses confirm
        const url = paymentIntentId
          ? `${API_URL}/api/notary/booking/${bookingId}/confirm-intent?payment_intent_id=${encodeURIComponent(paymentIntentId)}`
          : `${API_URL}/api/notary/booking/${bookingId}/confirm?session_id=${sessionId || ''}`;
        const confirmResponse = await fetch(url, { method: 'POST' });

        if (!confirmResponse.ok) {
          throw new Error('Failed to confirm booking');
        }

        const confirmData = await confirmResponse.json();
        setBooking(confirmData.booking);
      } catch (err) {
        console.error('Confirmation error:', err);
        // Try to fetch booking details anyway
        try {
          const bookingResponse = await fetch(`${API_URL}/api/notary/booking/${bookingId}`);
          if (bookingResponse.ok) {
            const bookingData = await bookingResponse.json();
            setBooking(bookingData);
          } else {
            setError('Could not retrieve booking details');
          }
        } catch {
          setError('Could not retrieve booking details');
        }
      } finally {
        setLoading(false);
      }
    };

    confirmBooking();
  }, [bookingId, sessionId, paymentIntentId]);

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const generateGoogleCalendarUrl = () => {
    if (!booking) return '#';
    
    const startDate = new Date(booking.appointment_datetime);
    const endDate = new Date(startDate.getTime() + (booking.duration || 30) * 60000);
    
    const formatForGoogle = (date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '');
    };
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `Notary Appointment - WesTex Legal`,
      dates: `${formatForGoogle(startDate)}/${formatForGoogle(endDate)}`,
      details: `Service: ${booking.notary_type_name}\nConfirmation #: ${booking.id}\n\nContact WesTex Legal:\nPhone: (806) 401-1749\nEmail: WesTexLegal@gmail.com`,
      location: booking.address,
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const generateICSFile = () => {
    if (!booking) return;
    
    const startDate = new Date(booking.appointment_datetime);
    const endDate = new Date(startDate.getTime() + (booking.duration || 30) * 60000);
    
    const formatForICS = (date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, -1) + 'Z';
    };
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//WesTex Legal//Notary Booking//EN
BEGIN:VEVENT
UID:${booking.id}@westexlegal.com
DTSTAMP:${formatForICS(new Date())}
DTSTART:${formatForICS(startDate)}
DTEND:${formatForICS(endDate)}
SUMMARY:Notary Appointment - WesTex Legal
DESCRIPTION:Service: ${booking.notary_type_name}\\nConfirmation #: ${booking.id}\\n\\nContact WesTex Legal:\\nPhone: (806) 401-1749\\nEmail: WesTexLegal@gmail.com
LOCATION:${booking.address}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `notary-appointment-${booking.id}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-[#ED1C24] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link
            to="/notary"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            <Home className="w-4 h-4" />
            Return to Booking
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#ED1C24] mx-auto mb-4" />
          <p className="text-white text-lg">Confirming your booking...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-confirmation, #printable-confirmation * {
            visibility: visible;
          }
          #printable-confirmation {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            color: black !important;
            padding: 20px;
          }
          #printable-confirmation .print-hide {
            display: none !important;
          }
          #printable-confirmation .bg-zinc-900,
          #printable-confirmation .bg-zinc-800,
          #printable-confirmation .bg-zinc-700 {
            background: white !important;
            border-color: #ddd !important;
          }
          #printable-confirmation .text-white,
          #printable-confirmation .text-zinc-400,
          #printable-confirmation .text-zinc-300 {
            color: black !important;
          }
          #printable-confirmation .text-green-500 {
            color: #22c55e !important;
          }
          #printable-confirmation .text-\\[\\#ED1C24\\] {
            color: #ED1C24 !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-black">
        {/* Header - Hidden on print */}
        <div className="bg-zinc-900 border-b border-zinc-700 py-8 px-4 text-center print-hide">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white">Booking Confirmed!</h1>
          <p className="text-zinc-400 mt-2">Your notary appointment has been scheduled</p>
        </div>

        {/* Printable Confirmation Details */}
        <div id="printable-confirmation" className="max-w-2xl mx-auto px-4 py-8">
          {/* Print Header - Only shows on print */}
          <div className="hidden print:block mb-6 text-center border-b pb-4">
            <h1 className="text-2xl font-bold">WesTex Legal Mobile Notary</h1>
            <p className="text-lg mt-2">Appointment Confirmation</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-xl overflow-hidden">
            {/* Confirmation Number */}
            <div className="bg-zinc-800 px-6 py-4 border-b border-zinc-700">
              <p className="text-sm text-zinc-400">Confirmation Number</p>
              <p className="text-2xl font-bold text-white font-mono">{booking?.id}</p>
            </div>

            {/* Appointment Details */}
            <div className="p-6 space-y-6">
              {/* Service */}
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#ED1C24]/20 rounded-lg print:bg-gray-100">
                  <FileText className="w-5 h-5 text-[#ED1C24]" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Service</p>
                  <p className="font-semibold text-white">{booking?.notary_type_name}</p>
                </div>
              </div>

              {/* Date & Time */}
              <div className="flex items-start gap-4">
                <div className="p-2 bg-zinc-700 rounded-lg print:bg-gray-100">
                  <Calendar className="w-5 h-5 text-zinc-300" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Date & Time</p>
                  <p className="font-semibold text-white">
                    {formatDateTime(booking?.appointment_datetime)}
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="p-2 bg-zinc-700 rounded-lg print:bg-gray-100">
                  <MapPin className="w-5 h-5 text-zinc-300" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Meeting Location</p>
                  <p className="font-semibold text-white">{booking?.address}</p>
                </div>
              </div>

              {/* Contact */}
              <div className="flex items-start gap-4">
                <div className="p-2 bg-zinc-700 rounded-lg print:bg-gray-100">
                  <User className="w-5 h-5 text-zinc-300" />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Contact Information</p>
                  <p className="font-semibold text-white">
                    {booking?.first_name} {booking?.last_name}
                  </p>
                  <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1">
                    <Mail className="w-3 h-3" /> {booking?.email}
                  </p>
                  <p className="text-sm text-zinc-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {booking?.phone}
                  </p>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 space-y-2 print:bg-gray-50 print:border-gray-200">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Service Fee</span>
                  <span className="font-medium text-white">${booking?.base_price?.toFixed(2)}</span>
                </div>
                {booking?.mileage_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Mileage Fee</span>
                    <span className="font-medium text-white">${booking?.mileage_fee?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-zinc-600 print:border-gray-300">
                  <span className="font-bold text-white">Total Paid</span>
                  <span className="font-bold text-green-500">${booking?.total_price?.toFixed(2)}</span>
                </div>
              </div>

              {/* Cancellation Policy */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 print:bg-yellow-50 print:border-yellow-300">
                <h4 className="font-medium text-amber-400 mb-1 print:text-yellow-700">Cancellation Policy</h4>
                {booking?.refundable ? (
                  <p className="text-sm text-amber-300/80 print:text-yellow-600">
                    To cancel or reschedule, you must do so at least {booking.cancellation_hours} hours before your appointment.
                  </p>
                ) : (
                  <p className="text-sm text-amber-300/80 print:text-yellow-600">
                    This is a rush appointment. No refunds are available.
                  </p>
                )}
              </div>

              {/* What to Expect - Hidden on print */}
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 print-hide">
                <h4 className="font-medium text-white mb-2">What to Expect</h4>
                <ul className="text-sm text-zinc-400 space-y-1">
                  <li>• A confirmation email has been sent to {booking?.email}</li>
                  <li>• You will receive a reminder before your appointment</li>
                  <li>• Please have a valid government-issued photo ID ready</li>
                  <li>• The notary will arrive at your specified location</li>
                </ul>
              </div>

              {/* Contact Info */}
              <div className="text-center pt-4 border-t border-zinc-700 print:border-gray-300">
                <p className="text-zinc-400 mb-2">Questions about your appointment?</p>
                <p className="font-medium text-white">
                  Call <a href="tel:8064011749" className="text-[#ED1C24] hover:underline">(806) 401-1749</a>
                  {' '}or email{' '}
                  <a href="mailto:WesTexLegal@gmail.com" className="text-[#ED1C24] hover:underline">WesTexLegal@gmail.com</a>
                </p>
              </div>
            </div>

            {/* Actions - Hidden on print */}
            <div className="bg-zinc-800 px-6 py-4 border-t border-zinc-700 print-hide">
              {/* Add to Calendar Row */}
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <a
                  href={generateGoogleCalendarUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add to Google Calendar
                </a>
                <button
                  onClick={generateICSFile}
                  className="flex-1 px-4 py-2.5 border border-zinc-600 text-white font-medium rounded-lg hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Download .ics File
                </button>
              </div>
              
              {/* Print & Home Row */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handlePrint}
                  className="flex-1 px-4 py-2.5 border border-zinc-600 text-white font-medium rounded-lg hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print Confirmation
                </button>
                <Link
                  to="/"
                  className="flex-1 px-4 py-2.5 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Return to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotaryConfirmation;
